import { useEffect, useState } from 'react';
import { Search, Loader2, Check, X, Unlink, Inbox } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { formatarCnpj, formatarDataHora } from './formato';
import ModalDesvincular from './ModalDesvincular';

const COR_STATUS = {
  pendente: 'bg-amber-100 text-amber-700',
  aprovada: 'bg-green-100 text-green-700',
  recusada: 'bg-red-100 text-red-700',
};

function Solicitacao({ s, ehAdmin, onDecidida }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function decidir(aprovar) {
    if (!aprovar && motivo.trim().length < 10) {
      toast.error('Escreva o motivo da recusa (mínimo 10 caracteres).');
      return;
    }
    const pergunta = s.tipo === 'desvincular'
      ? `Apagar o vínculo ${s.fornecedor_nome} / ${s.codigo_fornecedor} → ERP ${s.codigo_erp_atual}?${s.codigo_erp ? ` Em seguida será vinculado ao código ${s.codigo_erp}.` : ''}`
      : `Liberar o código ${s.codigo_erp} também para ${s.fornecedor_nome}?`;
    if (aprovar && !window.confirm(pergunta)) return;
    setEnviando(true);
    try {
      const resp = await apiService.precificacao.decidirSolicitacao(s.id, { aprovar, motivo: motivo.trim() });
      const aviso = resp.data.data?.aviso;
      if (aviso) toast.error(aviso, { duration: 8000 });
      else if (!aprovar) toast.success('Solicitação recusada.');
      else toast.success(s.tipo === 'desvincular' ? 'Vínculo desfeito.' : 'Liberação aprovada e vínculo criado.');
      onDecidida();
    } catch (error) {
      console.error('❌ Erro ao decidir solicitação:', error);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <li className="py-3 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${COR_STATUS[s.status]}`}>{s.status}</span>
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${s.tipo === 'desvincular' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
          {s.tipo === 'desvincular' ? 'Desvincular' : 'Liberar código'}
        </span>
        <strong>{s.fornecedor_nome}</strong>
        <span className="text-gray-500">({formatarCnpj(s.fornecedor_cnpj)})</span>
        <span className="text-gray-400">·</span>
        <span>produto <span className="font-mono">{s.codigo_fornecedor}</span> {s.descricao_fornecedor}</span>
      </div>
      {s.tipo === 'desvincular' ? (
        <p className="text-sm">
          vinculado hoje ao código <strong className="font-mono">{s.codigo_erp_atual}</strong> — {s.descricao_erp_atual}
          {s.codigo_erp
            ? <> · código correto sugerido: <strong className="font-mono">{s.codigo_erp}</strong> — {s.descricao_erp}</>
            : <span className="text-gray-500"> · sem código correto sugerido</span>}
        </p>
      ) : (
        <p className="text-sm">
          quer usar o código <strong className="font-mono">{s.codigo_erp}</strong> — {s.descricao_erp}
        </p>
      )}
      <p className="text-xs text-gray-600">
        <strong>Justificativa:</strong> {s.justificativa} · pedido por {s.solicitado_por} em {formatarDataHora(s.solicitado_em)}
      </p>
      {s.status !== 'pendente' && (
        <p className="text-xs text-gray-500">
          Decidido por {s.decidido_por} em {formatarDataHora(s.decidido_em)}{s.motivo_decisao ? ` — ${s.motivo_decisao}` : ''}
        </p>
      )}
      {ehAdmin && s.status === 'pendente' && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Motivo (obrigatório para recusar)"
            className="flex-1 min-w-[200px] px-2 py-1 text-sm border border-gray-300 rounded-md"
          />
          <button type="button" disabled={enviando} onClick={() => decidir(false)} className="flex items-center gap-1 px-3 py-1 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50">
            <X className="w-4 h-4" /> Recusar
          </button>
          <button type="button" disabled={enviando} onClick={() => decidir(true)} className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Aprovar
          </button>
        </div>
      )}
    </li>
  );
}

/** Aba "Vínculos" da Precificação: solicitações de liberação (admin decide) e vínculos gravados. */
export default function VinculosFornecedor({ onPendentesMudou }) {
  const { isAdmin } = useAuth();
  const ehAdmin = isAdmin();
  const [busca, setBusca] = useState('');
  const [vinculos, setVinculos] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [modalVinculo, setModalVinculo] = useState(null);

  async function carregar(termo) {
    setCarregando(true);
    try {
      const [respV, respS] = await Promise.all([
        apiService.precificacao.vinculos({ busca: termo }),
        apiService.precificacao.solicitacoes(),
      ]);
      const lista = respS.data.data || [];
      setVinculos(respV.data.data || []);
      setSolicitacoes(lista);
      onPendentesMudou?.(lista.filter(s => s.status === 'pendente').length);
    } catch (error) {
      console.error('❌ Erro ao carregar vínculos:', error);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar('');
  }, []); // carrega só ao abrir a aba

  // Admin desvincula direto; gerente vê o modal pedindo ao admin.
  function abrirDesvincular(v) {
    setModalVinculo({
      id: v.id,
      fornecedorNome: v.fornecedor_nome,
      codigoFornecedor: v.codigo_fornecedor,
      descricaoFornecedor: v.descricao_fornecedor,
      codigoErp: v.codigo_erp,
      descricaoErp: v.descricao_erp,
    });
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
          <Inbox className="w-5 h-5 text-amber-600" />
          {ehAdmin ? 'Solicitações de liberação' : 'Minhas solicitações de liberação'}
        </h3>
        <p className="text-xs text-gray-500 mb-2">
          Quando um código do ERP já pertence a outra razão social, só o admin pode liberar o uso por mais um fornecedor.
        </p>
        {solicitacoes.length === 0
          ? <p className="text-sm text-gray-500">Nenhuma solicitação.</p>
          : <ul className="divide-y divide-gray-100">{solicitacoes.map(s => <Solicitacao key={s.id} s={s} ehAdmin={ehAdmin} onDecidida={() => carregar(busca)} />)}</ul>}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-gray-900">Vínculos gravados (fornecedor → ERP)</h3>
        <form onSubmit={e => { e.preventDefault(); carregar(busca); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar por fornecedor, código do fornecedor, código ou nome no ERP"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Buscar</button>
        </form>

        {carregando && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Carregando…</p>}
        {!carregando && vinculos.length === 0 && <p className="text-sm text-gray-500">Nenhum vínculo encontrado.</p>}

        {vinculos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Fornecedor</th>
                  <th className="py-2 pr-3">Produto no fornecedor</th>
                  <th className="py-2 pr-3">Produto no ERP</th>
                  <th className="py-2 pr-3">Vinculado por</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {vinculos.map(v => (
                  <tr key={v.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                    <td className="py-2 pr-3">{v.fornecedor_nome}<br /><span className="text-xs text-gray-500">{formatarCnpj(v.fornecedor_cnpj)}</span></td>
                    <td className="py-2 pr-3"><span className="font-mono">{v.codigo_fornecedor}</span> {v.descricao_fornecedor}<br /><span className="text-xs text-gray-500">EAN {v.ean || '—'}</span></td>
                    <td className="py-2 pr-3">
                      <span className="font-mono font-semibold">{v.codigo_erp}</span> {v.descricao_erp}
                      {v.liberado_admin ? (
                        <p className="text-[11px] text-red-700">Liberado por {v.liberado_por}: {v.justificativa_liberacao}</p>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-600 whitespace-nowrap">{v.usuario}<br />{formatarDataHora(v.created_at)}</td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => abrirDesvincular(v)}
                        title={ehAdmin ? 'Desfazer este vínculo' : 'Vínculo errado? Peça ao admin para desfazer'}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                      >
                        <Unlink className="w-3.5 h-3.5" /> Desvincular
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalVinculo && (
        <ModalDesvincular
          vinculo={modalVinculo}
          onFechar={() => setModalVinculo(null)}
          onDesvinculado={() => carregar(busca)}
          onSolicitado={() => carregar(busca)}
        />
      )}
    </div>
  );
}
